import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Home, Truck, ChevronRight, Plus, Upload, Trash2, Loader2, X, Download, Building2, Sparkles, Share2, MessageCircle, Mail, Copy, Check, AlertTriangle, CheckCircle2, ShieldAlert, ShieldCheck, Clock, FileText } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { GstLookup, panFromGstin } from '../../components/GstLookup';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';


const COMMON_VENDOR_DOCS = [
  { key: 'PAN', label: 'PAN CARD', title: 'Vendor PAN Card', desc: 'Permanent Account Number copy of entity or proprietor', patterns: ['pan', 'pan card', 'copy of pan allotment'] },
  { key: 'GST', label: 'GST CERTIFICATE', title: 'GST Registration Certificate', desc: 'Form GST REG-06 or official registration copy', patterns: ['gst', 'gst copy', 'gst certificate', 'gstin'] },
  { key: 'BANK', label: 'BANK CANCELLED CHEQUE', title: 'Cancelled Cheque / Bank Proof', desc: 'Bank statement or cheque showing IFSC, A/C No. & Entity Name', patterns: ['bank', 'cheque', 'cancelled cheque', 'bank details', 'bank proof'] },
  { key: 'AADHAR', label: 'AADHAR CARD', title: 'Aadhar Card (Authorized Signatory)', desc: 'Aadhar Card of Proprietor, Partner or Director', patterns: ['aadhar', 'aadhaar', 'uidai', 'aadhar card'] },
  { key: 'INCORPORATION', label: 'INCORPORATION / REGISTRATION CERTIFICATE', title: 'COI / Partnership Deed / License', desc: 'Certificate of Incorporation, Partnership Deed, or Trade License', patterns: ['incorporation', 'certificate of incorporation', 'partnership deed', 'registration'] },
  { key: 'MSME', label: 'MSME / UDYAM REGISTRATION', title: 'MSME / Udyam Certificate', desc: 'Udyam Registration Certificate under MSMED Act (if applicable)', patterns: ['msme', 'udyam', 'udyam registration'] },
  { key: 'KYC_FORM', label: 'VENDOR KYC FORM / DECLARATION', title: 'Vendor Empanelment / KYC Form', desc: 'Signed & stamped Vendor Empanelment & KYC format', patterns: ['kyc form', 'declaration', 'vendor kyc', 'kyc'] },
  { key: 'AUTHORITY', label: 'POWER OF ATTORNEY / AUTHORITY LETTER', title: 'Authority Letter / Board Resolution', desc: 'Authority Letter or Board Resolution in favor of authorized signatory', patterns: ['power of attorney', 'authority letter', 'authorization', 'poa'] },
  { key: 'ADDRESS_PROOF', label: 'UTILITY / TELEPHONE BILL', title: 'Registered Office Address Proof', desc: 'Recent Electricity or Telephone bill proof of office address', patterns: ['telephone bill', 'utility bill', 'electricity bill', 'address proof'] },
  { key: 'IEC', label: 'IEC', title: 'IEC (Import Export Code)', desc: 'DGFT code for international logistics / clearing vendors', patterns: ['iec', 'import export code', 'ieccopy'] },
];

const AddVendor = () => {
  const navigate = useNavigate();
  const { id: vendorId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!vendorId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referenceUsers, setReferenceUsers] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [formData, setFormData] = useState({
    business_name: '',
    tax_preference: 'cgst_sgst',
    cin_number: '',
    pan_number: '',
    reference_by: '',
    remarks: '',
    is_active: true
  });

  const [branches, setBranches] = useState([{
    is_head_office: true,
    branch_nick_name: '',
    address_line_1: '',
    address_line_2: '',
    state: '',
    city: '',
    country: 'India',
    pincode: '',
    gst_number: '',
    contact_person: '',
    designation: '',
    contact_numbers: [''],
    emails: [''],
    is_active: true
  }]);

  const [kycDocuments, setKycDocuments] = useState([]);
  const [newDocName, setNewDocName] = useState('');
  const [newDocFile, setNewDocFile] = useState(null);
  const [showGstLookup, setShowGstLookup] = useState(false);
  const [fullVendorData, setFullVendorData] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [rekycModalOpen, setRekycModalOpen] = useState(false);
  const [rekycRemarks, setRekycRemarks] = useState('');
  const [submittingRekyc, setSubmittingRekyc] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleGstSelect = useCallback((record, autoBranches) => {
    const pan = record.pan || panFromGstin(record.gstin || '') || '';
    setFormData(prev => ({
      ...prev,
      business_name: record.legalName || record.tradeName || prev.business_name,
      pan_number: pan || prev.pan_number,
      cin_number: record.cin || record.cinNumber || prev.cin_number || '',
    }));
    setBranches(autoBranches.map(b => ({
      ...b,
      contact_person: '',
      designation: '',
      contact_numbers: [''],
      emails: [''],
    })));
    setShowGstLookup(false);
    const headBranch = autoBranches[0] || {};
    toast({
      title: 'GST data applied',
      description: `${autoBranches.length} branch${autoBranches.length > 1 ? 'es' : ''} filled. Head office: ${headBranch.city || headBranch.state || 'address populated'}.`,
    });
  }, [toast]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const getAuthHeadersFormData = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
  }), []);

  const fetchReferenceUsers = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/vendors/reference-users/list`, { headers: getAuthHeaders() });
      if (res.ok) setReferenceUsers(await res.json());
    } catch (e) { console.error(e); }
  }, [backendUrl, getAuthHeaders]);

  const checkDriveStatus = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/drive/status`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setDriveConnected(d.connected); }
    } catch (e) { console.error(e); }
  }, [backendUrl, getAuthHeaders]);

  const fetchVendor = useCallback(async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/vendors/${vendorId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Vendor not found');
      const data = await res.json();
      setFullVendorData(data);
      setFormData({
        business_name: data.business_name || '',
        tax_preference: data.tax_preference || 'cgst_sgst',
        cin_number: data.cin_number || '',
        pan_number: data.pan_number || '',
        reference_by: data.reference_by || '',
        remarks: data.remarks || '',
        is_active: data.is_active
      });
      if (data.branches?.length > 0) {
        setBranches(data.branches.map(b => ({
          id: b.id,
          is_head_office: b.is_head_office,
          branch_nick_name: b.branch_nick_name,
          address_line_1: b.address_line_1 || '',
          address_line_2: b.address_line_2 || '',
          state: b.state || '',
          city: b.city || '',
          country: b.country || 'India',
          pincode: b.pincode || '',
          gst_number: b.gst_number || '',
          contact_person: b.contact_person,
          designation: b.designation,
          contact_numbers: b.contact_numbers.length > 0 ? b.contact_numbers : [''],
          emails: b.emails.length > 0 ? b.emails : [''],
          is_active: b.is_active
        })));
      }
      if (data.kyc_documents) setKycDocuments(data.kyc_documents);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load vendor data", variant: "destructive" });
      navigate('/vendors');
    } finally { setLoading(false); }
  }, [vendorId, backendUrl, getAuthHeaders, toast, navigate]);

  useEffect(() => {
    fetchReferenceUsers();
    checkDriveStatus();
    if (isEditMode) fetchVendor();
  }, [fetchReferenceUsers, checkDriveStatus, fetchVendor, isEditMode]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleBranchChange = (index, field, value) => {
    setBranches(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u; });
  };

  const handleBranchArrayField = (bIdx, field, iIdx, value) => {
    setBranches(prev => {
      const u = [...prev];
      const arr = [...u[bIdx][field]];
      arr[iIdx] = value;
      u[bIdx] = { ...u[bIdx], [field]: arr };
      return u;
    });
  };

  const addArrayItem = (bIdx, field) => {
    setBranches(prev => {
      const u = [...prev];
      u[bIdx] = { ...u[bIdx], [field]: [...u[bIdx][field], ''] };
      return u;
    });
  };

  const removeArrayItem = (bIdx, field, iIdx) => {
    setBranches(prev => {
      const u = [...prev];
      const arr = u[bIdx][field].filter((_, i) => i !== iIdx);
      u[bIdx] = { ...u[bIdx], [field]: arr.length > 0 ? arr : [''] };
      return u;
    });
  };

  const addBranch = () => setBranches(prev => [...prev, {
    is_head_office: false, branch_nick_name: '', address_line_1: '', address_line_2: '',
    state: '', city: '', country: 'India', pincode: '', gst_number: '',
    contact_person: '', designation: '', contact_numbers: [''], emails: [''], is_active: true
  }]);

  const removeBranch = (index) => {
    if (branches.length <= 1) {
      toast({ title: "Error", description: "Vendor must have at least one branch", variant: "destructive" });
      return;
    }
    setBranches(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (!updated.some(b => b.is_head_office)) updated[0].is_head_office = true;
      return updated;
    });
  };

  const setAsHeadOffice = (index) => setBranches(prev => prev.map((b, i) => ({ ...b, is_head_office: i === index })));


  const handleRequestRekyc = async () => {
    if (!vendorId) return;
    try {
      setSubmittingRekyc(true);
      const res = await fetch(`${backendUrl}/api/vendors/${vendorId}/rekyc`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ remarks: rekycRemarks || 'Admin requested updated KYC documents' })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to request Re-KYC');
      }
      toast({
        title: 'Re-KYC Requested',
        description: 'Vendor portal is now unlocked for updates.'
      });
      setRekycModalOpen(false);
      setRekycRemarks('');
      fetchVendor();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSubmittingRekyc(false);
    }
  };

  const getPublicKycLink = () => {
    const token = fullVendorData?.kyc_token;
    return token ? `${window.location.origin}/vendor-kyc-onboarding/${token}` : '';
  };

  const copyKycLink = () => {
    const link = getPublicKycLink();
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast({ title: 'KYC Link Copied', description: link });
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareViaWhatsApp = () => {
    const link = getPublicKycLink();
    if (!link) return;
    const msg = encodeURIComponent(`Dear ${formData.business_name},\n\nPlease complete your Vendor Empanelment & KYC submission at:\n${link}\n\nThank you.`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const shareViaEmail = () => {
    const link = getPublicKycLink();
    if (!link) return;
    const subject = encodeURIComponent(`Vendor Empanelment & KYC Submission - ${formData.business_name}`);
    const body = encodeURIComponent(`Dear Team,\n\nPlease find the link below to submit your Vendor Empanelment & KYC documents online:\n\n${link}\n\nThank you,\nProcurement & Vendor Operations`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleUploadDocument = async () => {
    if (!newDocName || !newDocFile) {
      toast({ title: "Error", description: "Please enter document name and select a file", variant: "destructive" }); return;
    }
    if (!vendorId) {
      toast({ title: "Info", description: "Please save the vendor first before uploading documents" }); return;
    }
    try {
      setUploadingDoc(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', newDocFile);
      const res = await fetch(
        `${backendUrl}/api/vendors/${vendorId}/kyc-documents?document_name=${encodeURIComponent(newDocName)}`,
        { method: 'POST', headers: getAuthHeadersFormData(), body: formDataUpload }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to upload'); }
      const newDoc = await res.json();
      setKycDocuments(prev => [...prev, newDoc]);
      setNewDocName(''); setNewDocFile(null);
      toast({ title: "Success", description: "Document uploaded successfully" });
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to upload document", variant: "destructive" });
    } finally { setUploadingDoc(false); }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const res = await fetch(`${backendUrl}/api/vendors/${vendorId}/kyc-documents/${docId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete document');
      setKycDocuments(prev => prev.filter(d => d.id !== docId));
      toast({ title: "Success", description: "Document deleted successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete document", variant: "destructive" });
    }
  };

  const handleDownloadDocument = (doc) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${backendUrl}/api/drive/files/${doc.drive_file_id}/download?token=${token}`, '_blank');
  };

  const validateForm = () => {
    if (!formData.business_name.trim()) {
      toast({ title: "Validation Error", description: "Business name is required", variant: "destructive" }); return false;
    }
    for (let i = 0; i < branches.length; i++) {
      const b = branches[i];
      if (!b.branch_nick_name.trim()) { toast({ title: "Validation Error", description: `Branch ${i + 1}: Branch nick name is required`, variant: "destructive" }); return false; }
      if (!b.address_line_1.trim()) { toast({ title: "Validation Error", description: `Branch ${i + 1}: Address line 1 is required`, variant: "destructive" }); return false; }
      if (!b.contact_person.trim()) { toast({ title: "Validation Error", description: `Branch ${i + 1}: Contact person is required`, variant: "destructive" }); return false; }
      if (!b.designation.trim()) { toast({ title: "Validation Error", description: `Branch ${i + 1}: Designation is required`, variant: "destructive" }); return false; }
      if (!b.contact_numbers.some(n => n.trim())) { toast({ title: "Validation Error", description: `Branch ${i + 1}: At least one contact number is required`, variant: "destructive" }); return false; }
      if (!b.emails.some(e => e.trim())) { toast({ title: "Validation Error", description: `Branch ${i + 1}: At least one email is required`, variant: "destructive" }); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      const branchesData = branches.map(b => ({
        ...b, contact_numbers: b.contact_numbers.filter(n => n.trim()), emails: b.emails.filter(e => e.trim())
      }));
      const payload = { ...formData, reference_by: formData.reference_by || null, branches: branchesData };
      let res;
      if (isEditMode) {
        res = await fetch(`${backendUrl}/api/vendors/${vendorId}`, {
          method: 'PUT', headers: getAuthHeaders(),
          body: JSON.stringify({
            business_name: formData.business_name, tax_preference: formData.tax_preference,
            cin_number: formData.cin_number || null, pan_number: formData.pan_number || null,
            reference_by: formData.reference_by || null, remarks: formData.remarks || null, is_active: formData.is_active
          })
        });
      } else {
        res = await fetch(`${backendUrl}/api/vendors`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) });
      }
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to save vendor'); }
      const saved = await res.json();
      toast({ title: "Success", description: isEditMode ? "Vendor updated successfully" : "Vendor created successfully" });
      if (!isEditMode) navigate(`/vendors/edit/${saved.id}`);
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to save vendor", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const indianStates = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
    "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
    "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
    "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh","Puducherry","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Andaman and Nicobar Islands","Lakshadweep"
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      <span className="ml-2 text-text-secondary font-ibm">Loading vendor data...</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Home</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <Truck className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary cursor-pointer hover:text-accent-blue" onClick={() => navigate('/vendors')}>Vendors</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-accent-blue font-medium">{isEditMode ? 'Edit Vendor' : 'Add Vendor'}</span>
      </div>

      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-dark font-manrope">{isEditMode ? 'Edit Vendor' : 'Add Vendor'}</h1>
      </div>

      {/* Vendor Details Section */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-dark font-manrope">VENDOR DETAILS</h2>
          <Button
            type="button"
            data-testid="vendor-gst-lookup-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowGstLookup(v => !v)}
            className="text-accent-blue border-accent-blue/30 hover:bg-accent-blue/5 font-ibm text-xs font-semibold gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {showGstLookup ? 'Close GST Lookup' : 'Verify GST / PAN (Attestr)'}
          </Button>
        </div>

        {/* GST Lookup Panel (inline) */}
        {showGstLookup && (
          <div className="px-6 pt-4 pb-2">
            <GstLookup
              initialName={formData.business_name}
              onSelect={handleGstSelect}
              onClose={() => setShowGstLookup(false)}
            />
          </div>
        )}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Business Name<span className="text-accent-red ml-1">*</span></Label>
              <Input data-testid="vendor-business-name-input" placeholder="Vendor / Business Name" value={formData.business_name} onChange={e => handleInputChange('business_name', e.target.value)} className="border-gray-200 focus:border-accent-blue font-ibm" />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Tax Preference</Label>
              <Select value={formData.tax_preference} onValueChange={val => handleInputChange('tax_preference', val)}>
                <SelectTrigger data-testid="vendor-tax-preference-select" className="border-gray-200 font-ibm"><SelectValue placeholder="--Select--" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cgst_sgst">CGST/SGST</SelectItem>
                  <SelectItem value="igst">IGST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">CIN Number</Label>
              <Input data-testid="vendor-cin-input" placeholder="CIN Number" value={formData.cin_number} onChange={e => handleInputChange('cin_number', e.target.value)} className="border-gray-200 font-ibm" />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">PAN Number</Label>
              <Input data-testid="vendor-pan-input" placeholder="PAN Number" value={formData.pan_number} onChange={e => handleInputChange('pan_number', e.target.value.toUpperCase())} className="border-gray-200 font-ibm uppercase" maxLength={10} />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Reference By (Employee)</Label>
              <Select value={formData.reference_by || "none"} onValueChange={val => handleInputChange('reference_by', val === "none" ? '' : val)}>
                <SelectTrigger data-testid="vendor-reference-by-select" className="border-gray-200 font-ibm"><SelectValue placeholder="-Select Employee-" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {referenceUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.designation ? ` (${u.designation})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Status</Label>
              <Select value={formData.is_active ? 'active' : 'inactive'} onValueChange={val => handleInputChange('is_active', val === 'active')}>
                <SelectTrigger data-testid="vendor-status-select" className="border-gray-200 font-ibm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 lg:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Remarks</Label>
              <Textarea data-testid="vendor-remarks-textarea" placeholder="Remarks" value={formData.remarks} onChange={e => handleInputChange('remarks', e.target.value)} className="border-gray-200 font-ibm min-h-[80px]" />
            </div>
          </div>
        </div>
      </Card>

      {/* Branches */}
      {branches.map((branch, branchIndex) => (
        <Card key={branchIndex} className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-accent-blue" />
              <h2 className="text-lg font-semibold text-text-dark font-manrope">
                {branch.branch_nick_name
                  ? branch.branch_nick_name
                  : (branch.is_head_office ? 'Head Office' : `Branch ${branchIndex + 1}`)}
              </h2>
              {!branch.is_head_office && (
                <Button type="button" variant="outline" size="sm" onClick={() => setAsHeadOffice(branchIndex)} className="text-xs">Set as Head Office</Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {branchIndex === branches.length - 1 && (
                <Button data-testid="vendor-add-branch-btn" type="button" onClick={addBranch} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-9 px-4 rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />Add Branch
                </Button>
              )}
              {branches.length > 1 && (
                <Button type="button" variant="outline" onClick={() => removeBranch(branchIndex)} className="text-accent-red hover:text-accent-red hover:bg-accent-red/10 h-9"><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Branch Nick Name<span className="text-accent-red ml-1">*</span></Label>
                <Input data-testid={`vendor-branch-${branchIndex}-name`} placeholder="Branch Nick Name" value={branch.branch_nick_name} onChange={e => handleBranchChange(branchIndex, 'branch_nick_name', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Address Line 1<span className="text-accent-red ml-1">*</span></Label>
                <Input data-testid={`vendor-branch-${branchIndex}-address1`} placeholder="Address Line 1" value={branch.address_line_1} onChange={e => handleBranchChange(branchIndex, 'address_line_1', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Address Line 2</Label>
                <Input data-testid={`vendor-branch-${branchIndex}-address2`} placeholder="Address Line 2" value={branch.address_line_2} onChange={e => handleBranchChange(branchIndex, 'address_line_2', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">State</Label>
                <Select value={branch.state} onValueChange={val => handleBranchChange(branchIndex, 'state', val)}>
                  <SelectTrigger data-testid={`vendor-branch-${branchIndex}-state`} className="border-gray-200 font-ibm"><SelectValue placeholder="-Select State-" /></SelectTrigger>
                  <SelectContent>{indianStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">City</Label>
                <Input data-testid={`vendor-branch-${branchIndex}-city`} placeholder="City" value={branch.city} onChange={e => handleBranchChange(branchIndex, 'city', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Country</Label>
                <Input data-testid={`vendor-branch-${branchIndex}-country`} placeholder="Country" value={branch.country} onChange={e => handleBranchChange(branchIndex, 'country', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Pincode</Label>
                <Input data-testid={`vendor-branch-${branchIndex}-pincode`} placeholder="Pincode" value={branch.pincode} onChange={e => handleBranchChange(branchIndex, 'pincode', e.target.value)} className="border-gray-200 font-ibm" maxLength={6} />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">GST Number</Label>
                <Input data-testid={`vendor-branch-${branchIndex}-gst`} placeholder="GST Number" value={branch.gst_number} onChange={e => handleBranchChange(branchIndex, 'gst_number', e.target.value.toUpperCase())} className="border-gray-200 font-ibm uppercase" maxLength={15} />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Contact Person<span className="text-accent-red ml-1">*</span></Label>
                <Input data-testid={`vendor-branch-${branchIndex}-contact-person`} placeholder="Contact Person" value={branch.contact_person} onChange={e => handleBranchChange(branchIndex, 'contact_person', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Designation<span className="text-accent-red ml-1">*</span></Label>
                <Input data-testid={`vendor-branch-${branchIndex}-designation`} placeholder="Designation" value={branch.designation} onChange={e => handleBranchChange(branchIndex, 'designation', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Contact Numbers<span className="text-accent-red ml-1">*</span></Label>
                {branch.contact_numbers.map((phone, phoneIndex) => (
                  <div key={phoneIndex} className="flex gap-2">
                    <Input data-testid={`vendor-branch-${branchIndex}-phone-${phoneIndex}`} placeholder="Phone Number" value={phone} onChange={e => handleBranchArrayField(branchIndex, 'contact_numbers', phoneIndex, e.target.value)} className="border-gray-200 font-ibm" />
                    <Button type="button" variant="outline" size="icon" onClick={() => addArrayItem(branchIndex, 'contact_numbers')} className="shrink-0 bg-accent-orange hover:bg-accent-orange/90 text-white border-0"><Plus className="w-4 h-4" /></Button>
                    {branch.contact_numbers.length > 1 && <Button type="button" variant="outline" size="icon" onClick={() => removeArrayItem(branchIndex, 'contact_numbers', phoneIndex)} className="shrink-0"><X className="w-4 h-4" /></Button>}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Emails<span className="text-accent-red ml-1">*</span></Label>
                {branch.emails.map((email, emailIndex) => (
                  <div key={emailIndex} className="flex gap-2">
                    <Input data-testid={`vendor-branch-${branchIndex}-email-${emailIndex}`} type="email" placeholder="Email" value={email} onChange={e => handleBranchArrayField(branchIndex, 'emails', emailIndex, e.target.value)} className="border-gray-200 font-ibm" />
                    <Button type="button" variant="outline" size="icon" onClick={() => addArrayItem(branchIndex, 'emails')} className="shrink-0 bg-accent-orange hover:bg-accent-orange/90 text-white border-0"><Plus className="w-4 h-4" /></Button>
                    {branch.emails.length > 1 && <Button type="button" variant="outline" size="icon" onClick={() => removeArrayItem(branchIndex, 'emails', emailIndex)} className="shrink-0"><X className="w-4 h-4" /></Button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {/* Statutory KYC Documents */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-dark font-manrope">Statutory KYC Compliance</h2>
            <p className="text-xs text-text-secondary font-ibm">Common vendor verification documents (as applicable to vendor entity type)</p>
          </div>
          {isEditMode && fullVendorData?.kyc_token && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShareModalOpen(true)}
              className="text-xs font-semibold text-emerald-700 border-emerald-300 hover:bg-emerald-50 flex items-center gap-1.5"
            >
              <Share2 className="w-3.5 h-3.5" /> Share Public Link
            </Button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Statutory Document Slots */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COMMON_VENDOR_DOCS.map((docConfig) => {
              const uploaded = kycDocuments.find((d) => {
                const name = (d.document_name || '').toLowerCase().trim();
                return docConfig.patterns.some((p) => name.includes(p.toLowerCase()));
              });

              return (
                <div
                  key={docConfig.key}
                  className={`rounded-xl border p-4 flex flex-col justify-between transition-all ${
                    uploaded
                      ? 'bg-emerald-50/40 border-emerald-300'
                      : 'bg-white border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="font-semibold text-sm text-text-dark font-ibm flex items-center gap-2">
                        {uploaded ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                        )}
                        {docConfig.title}
                      </div>
                      {uploaded ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase shrink-0">
                          ✓ Attached
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase shrink-0">
                          As Applicable
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-secondary font-ibm mb-3">{docConfig.desc}</p>
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    {uploaded ? (
                      <div className="flex items-center justify-between text-xs font-ibm">
                        <span className="truncate pr-2 font-medium text-slate-800">{uploaded.file_name}</span>
                        <div className="flex gap-1.5 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadDocument(uploaded)}
                            className="h-7 px-2 text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" /> View
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument(uploaded.id)}
                            className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {isEditMode ? (
                          <label className="cursor-pointer inline-flex items-center justify-center w-full h-8 bg-blue-50/70 hover:bg-blue-100/90 text-blue-900 border border-blue-200 rounded-lg text-xs font-semibold font-ibm transition-colors">
                            <Upload className="w-3.5 h-3.5 mr-1.5" /> Attach {docConfig.label}
                            <input
                              type="file"
                              className="hidden"
                              disabled={uploadingDoc}
                              onChange={async (e) => {
                                if (e.target.files && e.target.files[0]) {
                                  const file = e.target.files[0];
                                  try {
                                    setUploadingDoc(true);
                                    const formDataUpload = new FormData();
                                    formDataUpload.append('file', file);
                                    const res = await fetch(
                                      `${backendUrl}/api/vendors/${vendorId}/kyc-documents?document_name=${encodeURIComponent(docConfig.label)}`,
                                      { method: 'POST', headers: getAuthHeadersFormData(), body: formDataUpload }
                                    );
                                    if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
                                    const newDoc = await res.json();
                                    setKycDocuments(prev => [...prev.filter(d => !docConfig.patterns.some(p => (d.document_name || '').toLowerCase().includes(p.toLowerCase()))), newDoc]);
                                    toast({ title: 'Success', description: `${docConfig.title} uploaded successfully` });
                                  } catch (err) {
                                    toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
                                  } finally {
                                    setUploadingDoc(false);
                                  }
                                }
                              }}
                            />
                          </label>
                        ) : (
                          <span className="text-[11px] text-text-secondary italic">Save vendor profile first to attach documents</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Custom Document Upload */}
          {isEditMode && (
            <div className="border-t border-gray-100 pt-5">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 font-ibm">Additional Custom Document</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-text-dark font-ibm">Document Title</Label>
                  <Input
                    placeholder="e.g. ISO Certificate, Insurance Policy"
                    value={newDocName}
                    onChange={(e) => setNewDocName(e.target.value)}
                    className="mt-1 text-sm font-ibm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-dark font-ibm">Select File</Label>
                  <Input
                    type="file"
                    onChange={(e) => setNewDocFile(e.target.files[0])}
                    className="mt-1 text-sm font-ibm"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={handleUploadDocument}
                    disabled={uploadingDoc || !newDocName || !newDocFile}
                    className="w-full bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 rounded-lg text-xs"
                  >
                    {uploadingDoc ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload Additional Doc</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Share KYC Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-manrope text-slate-900 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-emerald-600" /> Share Vendor KYC Portal
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 font-ibm">
              Send this secure onboarding link to the vendor. They can upload applicable KYC documents and branch contacts without login.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Vendor Public KYC Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  readOnly
                  value={getPublicKycLink()}
                  className="font-mono text-xs bg-slate-50 select-all"
                />
                <Button
                  type="button"
                  onClick={copyKycLink}
                  className="bg-accent-blue hover:bg-accent-blue/90 text-white shrink-0"
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                type="button"
                onClick={shareViaWhatsApp}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-ibm text-xs font-semibold h-10 rounded-lg flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </Button>
              <Button
                type="button"
                onClick={shareViaEmail}
                variant="outline"
                className="w-full border-slate-300 hover:bg-slate-50 text-slate-800 font-ibm text-xs font-semibold h-10 rounded-lg flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" /> Email
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShareModalOpen(false)}
              className="font-ibm text-xs"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Re-KYC Modal */}
      <Dialog open={rekycModalOpen} onOpenChange={setRekycModalOpen}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-manrope text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Request Re-KYC from Vendor
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 font-ibm">
              This will unlock the vendor's KYC portal so they can update branch contacts and upload corrected documents.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 my-2">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Remarks / Required Updates *</Label>
              <Textarea
                placeholder="e.g. Please provide updated GST certificate and Cancelled Cheque showing current account name."
                value={rekycRemarks}
                onChange={(e) => setRekycRemarks(e.target.value)}
                className="mt-1 text-sm font-ibm min-h-[90px]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRekycModalOpen(false)}
              className="font-ibm text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRequestRekyc}
              disabled={submittingRekyc}
              className="bg-amber-600 hover:bg-amber-700 text-white font-ibm text-xs font-semibold"
            >
              {submittingRekyc ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Confirm & Unlock KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4">
        <Button data-testid="vendor-cancel-btn" variant="outline" onClick={() => navigate('/vendors')} className="bg-gray-100 hover:bg-gray-200 text-text-dark border-0 font-ibm font-semibold h-11 px-8 rounded-lg">Cancel</Button>
        <Button data-testid="vendor-save-btn" onClick={handleSave} disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-8 rounded-lg">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : isEditMode ? 'Update Vendor' : 'Save Vendor'}
        </Button>
      </div>
    </div>
  );
};

export default AddVendor;
